/**
 * Expand Personalise on the order — copies every message in the cart into cart
 * attributes, so the order carries them as order attributes (Order > Additional
 * details in the admin, `attributes` in notification emails).
 *
 * Each message is already a line item property on its product (`Message`, set by
 * the `ex___personalise` block). This adds the order-level summary: one attribute
 * per item, labelled with the product (and its pack size when it has more than
 * one) and holding the message only. When a product has more than one message,
 * its labels are numbered; a line of 2 with the same message counts twice:
 *
 *   Vilafonte Series M 750ml (Single) – Personalisation 1: Happy birthday
 *   Vilafonte Series M 750ml (Single) – Personalisation 2: Happy birthday
 *   Vilafonte Series C 750ml (Single) – Personalisation: Congrats Sam
 *
 * One attribute each, rather than one with line breaks, because the admin's
 * Additional details shows an attribute on a single line, label above value.
 * Attributes left over from a bigger cart are cleared, and so are those written
 * by earlier versions of this file (`Personalisation`, `Personalisation 1 – ...`).
 *
 * Runs on every page (it is loaded from `layout/theme.liquid` through
 * `snippets/ex___personalise-order.liquid`), because the cart drawer can remove a
 * personalised item from any page. See `docs/personalise.md`.
 */

import { StandardEvents } from '@shopify/events';

/** The line item property the block writes. Keep in step with its "Order field name" setting. */
const PROPERTY = 'Message';

/** The cart attributes this writes end with a dash and this (plus a number when needed). */
const ATTRIBUTE = 'Personalisation';

/** This file's attributes, and those written by earlier versions of it. */
const OURS = new RegExp(`( – ${ATTRIBUTE}( \\d+)?$)|(^${ATTRIBUTE}( \\d+( – .*)?)?$)`);

const cartUrl = () => window.Theme?.routes?.cart_url ?? '/cart';

/**
 * Reads the current cart.
 * @returns {Promise<{ items: Array<{ quantity: number, product_title: string, variant_title: string | null, product_has_only_default_variant: boolean, properties: Record<string, string> | null }>, attributes: Record<string, string> } | null>}
 */
async function fetchCart() {
  try {
    const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

/** Brings the attributes in line with the cart, writing only when they differ. */
async function sync() {
  const cart = await fetchCart();
  if (!cart) return;

  const messages = [];
  for (const item of cart.items) {
    const message = item.properties?.[PROPERTY];
    if (!message) continue;

    const product =
      item.product_has_only_default_variant || !item.variant_title
        ? item.product_title
        : `${item.product_title} (${item.variant_title})`;
    for (let copy = 0; copy < item.quantity; copy++) messages.push({ product, message });
  }

  /**
   * How many messages each product has, so only repeated ones are numbered.
   * @type {Map<string, number>}
   */
  const totals = new Map();
  for (const { product } of messages) totals.set(product, (totals.get(product) ?? 0) + 1);

  /** @type {Record<string, string>} */
  const wanted = {};
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const { product, message } of messages) {
    const number = (counts.get(product) ?? 0) + 1;
    counts.set(product, number);
    const label = (totals.get(product) ?? 0) > 1 ? `${ATTRIBUTE} ${number}` : ATTRIBUTE;
    wanted[`${product} – ${label}`] = message;
  }

  // An empty value removes an attribute.
  /** @type {Record<string, string>} */
  const changes = {};
  for (const [key, value] of Object.entries(cart.attributes ?? {})) {
    if (OURS.test(key) && !(key in wanted) && value !== '') changes[key] = '';
  }
  for (const [key, value] of Object.entries(wanted)) {
    if (cart.attributes?.[key] !== value) changes[key] = value;
  }
  if (Object.keys(changes).length === 0) return;

  try {
    await fetch(`${cartUrl()}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: changes }),
    });
  } catch {
    // Next cart change tries again; the line item property is on the order regardless.
  }
}

/** @param {CustomEvent} event */
async function handleCartUpdate(event) {
  await event.detail?.promise?.catch(() => {});
  await sync();
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);

sync();
